import { useState } from 'react';
import axiosInstance from '@/api/axios';
import useAuth from '@/hooks/useAuth';
import type { SignupFormData } from '../schemas/signup.schema';

interface UseSignupReturn {
  signup: (data: SignupFormData) => Promise<void>;
  verifyEmail: (email: string) => Promise<boolean>;
  isSubmitting: boolean;
  error: string | null;
}

/**
 * Signup hook replicating AngularJS pages-signup.controller.ts
 *
 * Flow:
 * 1. Verify email uniqueness (POST /Companies/verifySignUpEmail)
 * 2. Create company (POST /Companies)
 * 3. Auto-login → activation guard redirects to KYC
 */
export function useSignup(): UseSignupReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { logIn } = useAuth();

  const verifyEmail = async (email: string): Promise<boolean> => {
    try {
      await axiosInstance.post('/Companies/verifySignUpEmail', { email });
      return true; // email is available
    } catch (err: any) {
      if (err?.response?.status === 423) {
        return false; // email taken
      }
      return false;
    }
  };

  const signup = async (formData: SignupFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // 1. Verify email
      const emailAvailable = await verifyEmail(formData.email);
      if (!emailAvailable) {
        setError('Este correo ya está registrado. Intenta con otro.');
        return;
      }

      // 2. Create company
      const companyData = {
        name: formData.name,
        email: formData.email,
        username: formData.email,
        password: formData.password,
        companyName: formData.companyName,
        country: formData.country,
        countryPrefix: formData.countryPrefix,
        cellphone: formData.cellphone,
        vendor: formData.vendor || undefined,
        referrer: formData.referrer || undefined,
        active: true,
        companyType: '1',
      };

      await axiosInstance.post('/Companies', companyData);

      // 3. Auto-login (getCurrent + activation guard will redirect to KYC)
      await logIn(formData.email, formData.password);
    } catch (err: any) {
      if (err?.response?.status === 422) {
        setError('Este correo ya está registrado.');
      } else {
        setError('Error al crear la cuenta. Intenta de nuevo.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return { signup, verifyEmail, isSubmitting, error };
}
